/*
 * Copyright (C) 2023 to Present Applause App Quality, Inc. All rights reserved.
 */

import { AbstractControl, AbstractControlOptions, FormArray, FormControlStatus, ValidationErrors } from '@angular/forms';
import { observeMethod } from '@ng-falcon/+utils/observe-method';
import { createNgfControlEnhancement } from '@ng-falcon/ngf-form/+model/enhancement/ngf-control-enhancement';
import { concat, defer, merge, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export type NgfFormArrayOptions = AbstractControlOptions & { disabled?: boolean; }

export class NgfFormArray<T extends AbstractControl> extends FormArray<T> {
  readonly controls$: Observable<Readonly<FormArray<T>['controls']>>;
  readonly value$: Observable<FormArray<T>['value']>;
  readonly errors$: Observable<ValidationErrors | null>;
  readonly touched$: Observable<boolean>;
  readonly untouched$: Observable<boolean>;
  readonly pristine$: Observable<boolean>;
  readonly dirty$: Observable<boolean>;
  readonly status$: Observable<FormControlStatus>;
  readonly valid$: Observable<boolean>;
  readonly invalid$: Observable<boolean>;
  readonly pending$: Observable<boolean>;
  readonly enabled$: Observable<boolean>;
  readonly disabled$: Observable<boolean>;

  constructor(controls: Array<T>, opts?: NgfFormArrayOptions) {
    super(controls, opts);

    const ngfControlEnhancement = createNgfControlEnhancement(this);
    this.value$ = ngfControlEnhancement.value$;
    this.errors$ = ngfControlEnhancement.errors$;
    this.touched$ = ngfControlEnhancement.touched$;
    this.untouched$ = ngfControlEnhancement.untouched$;
    this.pristine$ = ngfControlEnhancement.pristine$;
    this.dirty$ = ngfControlEnhancement.dirty$;
    this.status$ = ngfControlEnhancement.status$;
    this.valid$ = ngfControlEnhancement.valid$;
    this.invalid$ = ngfControlEnhancement.invalid$;
    this.pending$ = ngfControlEnhancement.pending$;
    this.enabled$ = ngfControlEnhancement.enabled$;
    this.disabled$ = ngfControlEnhancement.disabled$;

    this.controls$ = concat(
      defer(() => of([...this.controls] as Readonly<FormArray<T>['controls']>)),
      merge(
        observeMethod(this.controls, 'push'),
        observeMethod(this.controls, 'splice'),
      ).pipe(
        map(() => [...this.controls] as Readonly<FormArray<T>['controls']>)
      )
    );

    if (opts?.disabled) {
      this.disable();
    }
  }
}

